import {
  Flex,
  IconChevronRight,
  IconClock,
  IconTimer,
  RemoteImage,
} from '@banhala/ui-kit';
import { isNil, isNotNil } from 'shared/src/utils';
import type { ILogging } from 'shared/src/api/types/ILogging';
import { useOpenDeeplink } from 'shared/src/hooks';
import { useLogAnalyticsEvent } from 'shared/src/hooks/useLogAnalyticsEvent';
import { RollingNumberText } from './RollingNumberText';
import { useCountdownDisplay } from './hooks/useCountdownDisplay';
import { DIGIT_HEIGHT, promotionRollingTextStyle } from './textStyle';

type CountdownTextProps = {
  finishedAt: string;
  textColor: string;
};

/** 매초 갱신되는 카운트다운 표시부 — 여기서만 리렌더되도록 말단 격리 (상위 리렌더 방지) */
const CountdownText = ({ finishedAt, textColor }: CountdownTextProps) => {
  const { text } = useCountdownDisplay(finishedAt);

  // 'HH:MM:SS'/'N일' 숫자 자리는 롤링, ' 남음'은 정적 (RollingNumberText가 문자 단위 분기)
  return (
    <RollingNumberText
      text={`${text} 남음`}
      digitHeight={DIGIT_HEIGHT}
      textStyle={promotionRollingTextStyle(12, textColor)}
      mode={'countdown'}
    />
  );
};

export type PromotionCountdownProps = {
  finishedAt: string;
  variant: 'timer' | 'banner';
  textColor: string;
  backgroundColor: string;
  logoImage: { url: string; width: number; height: number } | null;
  /** non-empty면 D(탭 이동). null/''이면 chevron·탭 없음 */
  deeplink: string | null;
  /** D 탭 시 CLICK_EVENT로 발화할 서버 로깅. SCREEN/SESSION_KEY 등 공통은 AnalyticsContext가 주입 */
  logging: ILogging | null;
};

/**
 * PDP 가격 카드 상단 스트립/헤더.
 * - variant 'timer'(B): 로컬 시계 아이콘 + 카운트다운
 * - variant 'banner'(C/D): 서버 로고 + 카운트다운, deeplink 있으면(D) 좌측 로고 터치 이동
 */
export const PromotionCountdown = ({
  finishedAt,
  variant,
  textColor,
  backgroundColor,
  logoImage,
  deeplink,
  logging,
}: PromotionCountdownProps) => {
  const { openDeeplink } = useOpenDeeplink();
  const logAnalyticsEvent = useLogAnalyticsEvent();
  // 빈 문자열도 null과 동일 취급 — chevron 숨김·탭 불가.
  const actionableDeeplink =
    isNotNil(deeplink) && deeplink.length > 0 ? deeplink : null;

  const handlePressBanner = () => {
    if (isNil(actionableDeeplink)) return;
    // 서버 logging 있을 때만 CLICK_EVENT
    if (isNotNil(logging)) {
      logAnalyticsEvent({ name: 'CLICK_EVENT', logging });
    }
    openDeeplink(actionableDeeplink);
  };

  if (variant === 'timer') {
    return (
      <Flex.Row
        alignItems={'center'}
        gap={4}
        padding={{ horizontal: 12, vertical: 6 }}
        backgroundColorValue={backgroundColor}
      >
        <IconClock size={16} fill={textColor} />
        <CountdownText finishedAt={finishedAt} textColor={textColor} />
      </Flex.Row>
    );
  }

  return (
    <Flex.Row
      alignItems={'center'}
      justifyContent={'space-between'}
      padding={{ horizontal: 12, vertical: 8 }}
      backgroundColorValue={backgroundColor}
    >
      <Flex.Row
        alignItems={'center'}
        gap={2}
        onPress={
          isNotNil(actionableDeeplink) ? handlePressBanner : undefined
        }
      >
        {isNotNil(logoImage) && (
          <RemoteImage
            uri={logoImage.url}
            width={logoImage.width}
            height={logoImage.height}
          />
        )}
        {isNotNil(actionableDeeplink) && (
          <IconChevronRight fill={textColor} size={16} />
        )}
      </Flex.Row>
      <Flex.Row alignItems={'center'} gap={4}>
        <IconTimer fill={textColor} size={16} />
        <CountdownText finishedAt={finishedAt} textColor={textColor} />
      </Flex.Row>
    </Flex.Row>
  );
};
